import type { AxiosInstance } from 'axios';
import type {
  GetConfigTemplatesResponse,
  GetConfigTemplatesParams,
  FlowReadiness,
  FlowCatalogParams,
  FlowCatalogResponse,
  FlowDefinition,
} from './contracts/chat-adapter';

/**
 * Gateway for interacting with the Chat Adapter API.
 */
export class ChatAdapterGateway {
  constructor(
    private readonly httpClient: AxiosInstance,
    private readonly baseUrl: string,
  ) {}

  /**
   * Retrieves message templates associated with a configuration.
   * @param configId - The unique identifier of the configuration.
   * @param params - Optional query parameters for filtering and pagination.
   * @returns A promise that resolves to the template list response.
   */
  async getConfigTemplates(configId: string, params?: GetConfigTemplatesParams): Promise<GetConfigTemplatesResponse> {
    const { data } = await this.httpClient.get<GetConfigTemplatesResponse>(
      `${this.baseUrl}/message-templates/${configId}`,
      { params },
    );
    return data;
  }

  async getFlowReadiness(configId: string): Promise<FlowReadiness> {
    const { data } = await this.httpClient.get<FlowReadiness>(`${this.baseUrl}/flows/readiness/${configId}`);
    return data;
  }

  async prepareFlowChannel(configId: string): Promise<FlowReadiness> {
    const { data } = await this.httpClient.put<FlowReadiness>(`${this.baseUrl}/flows/readiness/${configId}`);
    return data;
  }

  async getFlowCatalog(configId: string, params?: FlowCatalogParams): Promise<FlowCatalogResponse> {
    const { data } = await this.httpClient.get<FlowCatalogResponse>(`${this.baseUrl}/flows/catalog/${configId}`, {
      params,
    });
    return data;
  }

  async getFlowDefinition(configId: string, metaFlowId: string): Promise<FlowDefinition> {
    const { data } = await this.httpClient.get<FlowDefinition>(
      `${this.baseUrl}/flows/catalog/${configId}/${metaFlowId}`,
    );
    return data;
  }
}

export type {
  GetConfigTemplatesResponse,
  GetConfigTemplatesParams,
  MessageTemplate,
  MessageTemplateComponent,
  ConfigTemplatesPaging,
  FlowReadiness,
  FlowCatalogItem,
  FlowCatalogParams,
  FlowCatalogResponse,
  FlowDefinition,
} from './contracts/chat-adapter';
